import { useEffect, useRef, useState } from 'react'
import { Calendar } from 'primereact/calendar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons'
import { formatDateToISO } from '../../../utils/helpers/date.helpers'
import classes from './CalendarRefiner.module.scss'

const dateTypes = [
    { condition: 'DateFrom', label: 'From' },
    { condition: 'DateTo', label: 'To' },
]

const CalendarRefiner = props => {
    const [isShow, setIsShow] = useState(false)
    const [dateValues, setDateValues] = useState({})
    const appliedConditions = useRef([])
    const skipNextSync = useRef(false)

    const {
        refinerLabel,
        refinerId,
        onChangeDatePickerRefiner,
        onClickRemoveRefinerGroup,
        refiners,
    } = props

    useEffect(() => {
        const conditions = refiners.filter(i => i.Id === refinerId).map(i => i.Condition)

        // The update originated from this component, so the inputs already hold the latest values
        if (skipNextSync.current) {
            skipNextSync.current = false
            appliedConditions.current = conditions
            return
        }

        const isStartDateRemoved =
            appliedConditions.current.includes('DateFrom') && !conditions.includes('DateFrom')

        setDateValues(prevState => {
            // Clearing the start date invalidates the whole range
            if (isStartDateRemoved) return {}

            const newState = { ...prevState }
            dateTypes.forEach(({ condition }) => {
                if (
                    appliedConditions.current.includes(condition) &&
                    !conditions.includes(condition)
                ) {
                    newState[condition] = null
                }
            })
            return newState
        })

        appliedConditions.current = conditions

        // A range without its start date is not a valid filter, so drop what is left of the group
        if (conditions.length && !conditions.includes('DateFrom')) {
            onClickRemoveRefinerGroup(refinerId)
        }
    }, [refiners, refinerId, onClickRemoveRefinerGroup])

    const isDateDisabled = condition => condition === 'DateTo' && !dateValues.DateFrom

    const handleChange = (date, condition) => {
        const nextValues = { ...dateValues, [condition]: date }
        // Clearing the start date invalidates the whole range
        if (condition === 'DateFrom' && !date) {
            nextValues.DateTo = null
        }
        setDateValues(nextValues)

        const { DateFrom, DateTo } = nextValues

        if (DateFrom && DateTo) {
            const fromString = formatDateToISO(new Date(DateFrom).toDateString())
            const toString = formatDateToISO(new Date(DateTo).toDateString())

            skipNextSync.current = true
            // Send both bounds in one call so the list is fetched only once
            onChangeDatePickerRefiner([
                {
                    Value: fromString,
                    Condition: 'DateFrom',
                    DisplayText: 'Date From ' + fromString,
                },
                {
                    Value: toString,
                    Condition: 'DateTo',
                    DisplayText: 'Date To ' + toString,
                },
            ])
        } else if (appliedConditions.current.length || refiners.some(i => i.Id === refinerId)) {
            skipNextSync.current = true
            appliedConditions.current = []
            onClickRemoveRefinerGroup(refinerId)
        }
    }

    const isFieldCleared = (event, condition) =>
        event.target.tagName === 'INPUT' &&
        !/\d/.test(event.target.value ?? '') &&
        (dateValues[condition] || appliedConditions.current.includes(condition))

    // Read on the capture phase: Calendar restores the previous text in its own blur handler
    const handleBlur = (event, condition) => {
        if (isFieldCleared(event, condition)) handleChange(null, condition)
    }

    // The mask swallows some change events, so emptying the field is detected here instead
    const handleKeyUp = (event, condition) => {
        if (isFieldCleared(event, condition)) handleChange(null, condition)
    }

    const handleCalendarChange = (event, condition) => {
        const rawValue = event.originalEvent?.target?.value
        // A half-deleted mask can still parse into a date, which would re-apply the filter
        const isIncomplete = typeof rawValue === 'string' && rawValue.includes('_')

        handleChange(isIncomplete ? null : event.value, condition)
    }

    return (
        <div className="ul-card -app">
            <div
                className="ul-card__item -bordered ul-card__head cursor-pointer s-refiner-height s-container-text"
                onClick={() => setIsShow(prevState => !prevState)}
            >
                <span className="font-size-14 font-weight-semi-bold">{refinerLabel}</span>
                <span className="float-right">
                    <FontAwesomeIcon icon={isShow ? faCaretDown : faCaretRight} />
                </span>
            </div>

            {isShow && (
                <div className={`ul-card__item ul-card__body ${classes['calendars-container']}`}>
                    {dateTypes.map(({ condition, label }) => (
                        <div
                            key={condition}
                            className={classes['calendar-field']}
                            onBlurCapture={e => handleBlur(e, condition)}
                            onKeyUp={e => handleKeyUp(e, condition)}
                        >
                            <label htmlFor={condition}>{label}</label>
                            <Calendar
                                id={condition}
                                key={`${condition}-${condition === 'DateTo' && dateValues.DateFrom ? 'enabled' : 'default'}`}
                                disabled={isDateDisabled(condition)}
                                value={dateValues[condition] ? new Date(dateValues[condition]) : null}
                                onChange={e => handleCalendarChange(e, condition)}
                                showIcon
                                placeholder="yyyy-mm-dd"
                                dateFormat="yy-mm-dd"
                                mask="9999-99-99"
                                slotChar="_"
                                maxDate={condition === 'DateFrom' && dateValues.DateTo ? new Date(dateValues.DateTo) : null}
                                minDate={condition === 'DateTo' && dateValues.DateFrom ? new Date(dateValues.DateFrom) : null}
                                monthNavigator
                                yearNavigator
                                yearRange="2020:3000"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default CalendarRefiner
